import { useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Image } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import "dayjs/locale/en";
import { LangContext } from "../../contexts/Contexts";
import { DEFAULT_PROFILE_IMAGE } from "../../../infrastructure/utils/normalizeImageSrc";
import { EVENT_COMMENT_MAX_LENGTH } from "../../../shared/constants/textLimits";
import { esIdiomaEspanol } from "./commentsTexts";

// Un comentario de la pila — avatar, nombre, fecha/hora, contenido, las imagenes
// adjuntas en miniatura expandible, el "me gusta" y el menu de editar/eliminar.
//
// NO HAY RESPUESTAS A UN COMENTARIO, y no es un olvido. En eventos existio un hilo
// de un solo nivel que vivia unicamente en el estado de la pantalla:
// `InsertarComentario` no tiene `parentCommentId` ni ningun otro endpoint que lo
// soporte, asi que una respuesta se veia hasta que la persona recargaba y
// desaparecia. Se retiro. Si alguna vez hay contrato para responder —el foro SI lo
// tiene, con `InsertarComentarioPost`— esto se vuelve a construir contra el, no
// reviviendo un hilo que solo existia en memoria.
//
// El "hace X" se calcula con dayjs porque es lo que ya usa el resto de la
// aplicacion y porque `date-fns/locale` es ESM y el jest de CRA no lo transforma.
dayjs.extend(relativeTime);

// Iconos del feed, reusados tal cual: la reaccion de un comentario tiene que verse
// igual en todas las pantallas.
const ICONO_SIN_REACCION = "/img/icons/react-pub-grey.png";
const ICONO_CON_REACCION = "/img/icons/reacted-pub.png";

export const CommentItem = ({
  idComentario,
  autor,
  foto,
  fecha,
  texto,
  imagenes = [],
  totalReacciones = 0,
  reaccionActiva = false,
  editado = false,
  // Quien puede hacer que. Se deciden ARRIBA, en la seccion, porque dependen de la
  // sesion y del recurso; aqui solo se pintan. Editar es solo del autor —nadie
  // reescribe lo que dijo otro—; eliminar tambien lo puede hacer quien modera.
  puedeEditar = false,
  puedeEliminar = false,
  // Con `false` el boton de me gusta ni se pinta: hay recursos cuyo backend no
  // tiene reaccion por comentario, y un boton que no guarda nada es peor que no
  // tenerlo.
  puedeReaccionar = true,
  onEditar,
  onEliminar,
  // Devuelve el agregado del comentario ya recalculado por el backend, o lanza si
  // no se pudo guardar. La llamada la hace la seccion; aqui solo se pinta.
  onReaccionar,
  maxCaracteres = EVENT_COMMENT_MAX_LENGTH,
  textos = {},
}) => {
  const { lang } = useContext(LangContext);
  const isSpanish = esIdiomaEspanol(lang);

  // "Me gusta" del comentario. Se copia la semantica del feed: un boton que alterna
  // y un contador al lado, no el catalogo de cuatro emojis del recurso.
  //
  // El estado ARRANCA en lo que diga el listado y se mantiene aqui en local porque
  // el gesto se pinta antes de que conteste el servidor.
  const [meGusta, setMeGusta] = useState(reaccionActiva);
  const [totalMeGusta, setTotalMeGusta] = useState(totalReacciones);

  // Y se vuelve a sincronizar cuando el agregado cambia ARRIBA, en la lista: un
  // `useState` solo lee su valor inicial al montar, asi que sin esto la tarjeta se
  // quedaria pegada a lo que trajo el primer render. Pasa de verdad, porque la pila
  // se pagina en cliente: ir a la pagina siguiente y volver remonta la tarjeta, y
  // tambien cuando el listado se vuelve a pedir. El estado optimista no se pierde
  // por esto: mientras la peticion viaja las props todavia no cambiaron, y cuando
  // cambian traen justo lo que contesto el servidor.
  useEffect(() => {
    setMeGusta(reaccionActiva);
    setTotalMeGusta(totalReacciones);
  }, [reaccionActiva, totalReacciones]);

  // Turno de la ultima peticion enviada. NO se bloquea el boton mientras una esta en
  // vuelo —presionar dos veces rapido y que la segunda no haga nada se lee como que
  // la pantalla se trabo—; lo que se hace es descartar las respuestas que ya
  // quedaron viejas, para que la que pinte sea siempre la del ultimo clic.
  const turnoReaccion = useRef(0);

  // Edicion en linea: el comentario se convierte en su propio formulario. Se
  // prefiere a un modal porque el usuario sigue viendo el hilo alrededor y no
  // pierde el contexto de lo que estaba comentando.
  const [editando, setEditando] = useState(false);
  const [textoEditado, setTextoEditado] = useState(texto ?? "");
  const [guardando, setGuardando] = useState(false);

  const abrirEdicion = () => {
    setTextoEditado(texto ?? "");
    setEditando(true);
  };

  const cancelarEdicion = () => {
    setEditando(false);
    setTextoEditado(texto ?? "");
  };

  const edicionValida =
    textoEditado.trim().length > 0 && textoEditado.trim() !== (texto ?? "").trim();

  const guardarEdicion = async () => {
    if (!edicionValida || guardando) return;
    try {
      setGuardando(true);
      await onEditar?.(idComentario, textoEditado.trim());
      setEditando(false);
    } finally {
      // Se libera el boton pase lo que pase: si el guardado fallo, la seccion ya
      // mostro el aviso y el usuario debe poder reintentar sin recargar.
      setGuardando(false);
    }
  };

  const acciones = [];
  if (puedeEditar) {
    acciones.push({ key: "editar", label: textos.editar, onClick: abrirEdicion });
  }
  if (puedeEliminar) {
    acciones.push({
      key: "eliminar",
      label: textos.eliminar,
      danger: true,
      onClick: () => onEliminar?.(idComentario),
    });
  }

  // El boton se pinta de inmediato y se corrige con lo que conteste el backend: una
  // reaccion es un gesto, y esperar la respuesta para mover el icono se siente
  // trabado. Si la peticion falla se regresa al estado anterior, para no dejar en
  // pantalla un "me gusta" que no se guardo.
  const alternarMeGusta = async () => {
    const activoPrevio = meGusta;
    const totalPrevio = totalMeGusta;

    setMeGusta(!activoPrevio);
    setTotalMeGusta(
      activoPrevio ? Math.max(0, totalPrevio - 1) : totalPrevio + 1
    );

    if (typeof onReaccionar !== "function") return;

    const miTurno = (turnoReaccion.current += 1);

    try {
      const resultado = await onReaccionar(idComentario);
      if (miTurno !== turnoReaccion.current) return;
      // La respuesta trae el agregado ya recalculado por el servidor, asi que
      // manda sobre la suma optimista: si otra persona reacciono mientras tanto,
      // el numero queda bien sin volver a pedir el listado.
      if (resultado) {
        setMeGusta(Boolean(resultado.activa));
        setTotalMeGusta(Number(resultado.total) || 0);
      }
    } catch (error) {
      if (miTurno !== turnoReaccion.current) return;
      setMeGusta(activoPrevio);
      setTotalMeGusta(totalPrevio);
    }
  };

  const date = dayjs(fecha);
  const timeAgo = date.isValid()
    ? date.locale(isSpanish ? "es" : "en").fromNow()
    : "";

  return (
    <article className="vn-comment">
      <img
        className="vn-comment__avatar"
        src={foto || DEFAULT_PROFILE_IMAGE}
        alt=""
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = DEFAULT_PROFILE_IMAGE;
        }}
      />
      <div className="vn-comment__main">
        <div className="vn-comment__body">
          <div className="vn-comment__header">
            <span className="vn-comment__author">{autor}</span>
            {timeAgo && (
              <time className="vn-comment__time" dateTime={fecha}>
                {timeAgo}
              </time>
            )}
            {editado && (
              <span className="vn-comment__edited">({textos.marcaEditado})</span>
            )}

            {acciones.length > 0 && !editando && (
              /* El menu solo existe si hay al menos una accion permitida: un boton
                 que abre un menu vacio se lee como algo roto. */
              <Dropdown
                menu={{ items: acciones }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <button
                  type="button"
                  className="vn-comment__actions-trigger"
                  aria-label={textos.acciones}
                  title={textos.acciones}
                >
                  <MoreOutlined />
                </button>
              </Dropdown>
            )}
          </div>

          {editando ? (
            <div className="vn-comment__edit-box">
              <textarea
                className="vn-comment-input"
                aria-label={textos.editando}
                maxLength={maxCaracteres}
                rows={3}
                value={textoEditado}
                onChange={(event) => setTextoEditado(event.target.value)}
                disabled={guardando}
                autoFocus
              />
              <div className="vn-comment__edit-actions">
                <Button type="text" size="small" onClick={cancelarEdicion}>
                  {textos.cancelar}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  className="vn-comment-submit"
                  disabled={!edicionValida}
                  loading={guardando}
                  onClick={guardarEdicion}
                >
                  {textos.guardar}
                </Button>
              </div>
            </div>
          ) : (
            texto && <p className="vn-comment__text">{texto}</p>
          )}

          {imagenes.length > 0 && (
            /* Miniaturas expandibles al hacer clic. El visor de antd las agrupa,
               asi que desde una se puede pasar a las demas. */
            <Image.PreviewGroup>
              <ul className="vn-comment__images">
                {imagenes.map((imagen) => (
                  <li key={imagen.id}>
                    <Image
                      src={imagen.url}
                      alt={imagen.nombre}
                      className="vn-comment__image"
                    />
                  </li>
                ))}
              </ul>
            </Image.PreviewGroup>
          )}
        </div>

        {/* Estilo Facebook: la fila de acciones cuelga del comentario al que
            pertenece, justo bajo su cuerpo. */}
        {puedeReaccionar && (
          <div className="vn-comment__actions">
            <button
              type="button"
              className={`vn-comment__like ${meGusta ? "is-active" : ""}`}
              aria-pressed={meGusta}
              aria-label={textos.meGusta}
              title={textos.meGusta}
              onClick={alternarMeGusta}
            >
              {/* Decorativo: el nombre accesible ya lo da el aria-label del boton
                  y el estado el aria-pressed, asi que el icono se oculta del arbol
                  de accesibilidad. El testid es para poder fijar en prueba que la
                  ruta del asset es la del feed. */}
              <span className="vn-comment__like-icon">
                <img
                  src={meGusta ? ICONO_CON_REACCION : ICONO_SIN_REACCION}
                  alt=""
                  aria-hidden="true"
                  data-testid="vn-comment-like-icon"
                />
              </span>
              <span className="vn-comment__like-count">{totalMeGusta}</span>
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

export default CommentItem;
